import { change, clearFields } from 'redux-form';
import { Type } from './types';
import { storage } from 'app/util/storage';
import { FieldNames } from 'app/models/fields/names';
import { borrowerSsnRef, coBorrowerSsnRef } from 'app/components/FormFields/Ssn/CollectSsnBase';
import { ajaxVerificationDeleteAllCustomers, ajaxVerificationDeleteAllCustomersFail, ajaxVerificationDeleteAllCustomersSuccess } from 'app/actions/form/verification/actions';
import { deleteAllCustomers } from 'app/api/verification/financial';

/**
 * Iterates over the test case data selected by the DevCreditPull component
 * to be used in the soft pull API on staging. The following are the main
 * fields that are important for the API (in the testing environment):
 * * First Name
 * * Middle Name
 * * Last Name
 * * Street, City, State, Zip
 * * SSN (Not used in production, only being used to trigger test cases on the API)
 *
 *
 * @export
 * @param {*} payload
 * @returns
 */
export function setCreditPullTestCase(payload: any) {
  return (dispatch, getState) => {
    dispatch({ type: Type.SET_CREDIT_PULL_TEST_CASE, payload });
    const formStore = getState().form; // active forms
    Object.keys(formStore).forEach((formName) => {
      Object.keys(payload).forEach((key) => {
        dispatch(change(formName, key, payload[key]));
        dispatch(clearFields(formName, false, false, FieldNames.creditReportId));
      });
    });
  };
}

/**
 * This method sets the test cases for Finicity into the current form
 * that we're on.  It also populates the SSN reference we have with
 * the correct Finicity test case SSN as well.  This method will work
 * for both the borrower or co-borrower based on the flag.
 *
 * @param {any} payload
 * @param {boolean} isCoBorrower
 * @returns
 */
export function setFinicityTestCase(payload: any, isCoBorrower?: boolean) {
  return (dispatch, getState) => {
    dispatch({ type: Type.SET_FINICITY_TEST_CASE, payload });
    const formStore = getState().form; // active forms
    Object.keys(formStore).forEach((formName) => {
      if (isCoBorrower) {
        dispatch(change(formName, FieldNames.coBorrowerDOB, payload[FieldNames.dateOfBirth]));
      } else {
        dispatch(change(formName, FieldNames.dateOfBirth, payload[FieldNames.dateOfBirth]));
      }
    });

    // Hack to get React onChange to fire
    // https://github.com/facebook/react/issues/10135#issuecomment-500929024
    if (isCoBorrower && coBorrowerSsnRef) {
      const previousValue = coBorrowerSsnRef.value;
      coBorrowerSsnRef.value = payload.ssn;
      const tracker = coBorrowerSsnRef._valueTracker;
      if (tracker) {
        tracker.setValue(previousValue);
      }
      coBorrowerSsnRef.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (borrowerSsnRef) {
      const previousValue = borrowerSsnRef.value;
      borrowerSsnRef.value = payload.ssn;
      const tracker = borrowerSsnRef._valueTracker;
      if (tracker) {
        tracker.setValue(previousValue);
      }
      borrowerSsnRef.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

/**
 * Sets or removes an item to localStorage for bypassing refer to LO scenarios.
 */
export function toggleReferToLoBypass(isEnabled: boolean) {
  return (dispatch, getState) => {
    dispatch({ type: Type.TOGGLE_REFER_TO_LO_BYPASS, isEnabled });
    if (isEnabled) {
      storage.local.setItem('referToLoBypass', 'enabled');
    } else {
      storage.local.removeItem('referToLoBypass');
    }
  };
}

/**
 * Deletes all "testing" customers from Finicity.  Only used on dev tools.
 */
export function ajaxDeleteAllFinicityCustomers() {
  return async (dispatch) => {
    dispatch(ajaxVerificationDeleteAllCustomers());
    return deleteAllCustomers()
      .then((response) => {
        dispatch(ajaxVerificationDeleteAllCustomersSuccess(response));
      })
      .catch((error) => {
        dispatch(ajaxVerificationDeleteAllCustomersFail(error));
      });
  };
}
